import { useEffect, useRef, useState } from 'react'
import { ChevronDown, GaugeHigh, GaugeLow, GaugeMedium, IconComponent } from '@digital-twins/design-system/icons'

import { AssistantEffort } from '@digital-twins/shared-types'

import { cn } from '@/lib/utils'

import {
  ComposerAppearance,
  composerPanelClassName,
  composerTriggerCaptionClassName,
  composerTriggerChevronClassName,
  composerTriggerClassName,
} from './composerAppearance'

/**
 * Effort control — a single `Effort` button that opens a Low / Medium / High
 * menu, default Medium.
 *
 * A JOIN: this exact component (same name, same path) is mounted by BOTH
 * composers per the Assistant Platform v1 execution plan, Step 6.2 (home
 * composer) and Step 6.3 (thread composer). The join props are `value`,
 * `onChange`, `className`, and `appearance` (`internal` | `external`, default
 * `external`). Appearance is now part of the join so Home Lab (inside the
 * box) and the thread row (under the box) can look different without forking
 * the control.
 *
 * Maps to the per-turn `effort` parameter (`AssistantEffort` in
 * `packages/shared-types/src/assistant.ts`), which the brain route resolves
 * to its `reasoningEffort`. No explanation copy: the three labels are the
 * whole surface, deliberately microcopy-minimal.
 *
 * Shape: a menu, not the three-up segmented pill it started as. Both
 * composers now sit the control OUTSIDE the composer box, in a quiet row of
 * chrome beside the knowledge scope, where three permanently-visible options
 * shout louder than the one answer they collapse to. Collapsing to a trigger
 * that names the current level keeps the row readable and leaves room for the
 * neighbouring context controls.
 *
 * Click-outside-to-close is copied from `WorkflowMenu`, its neighbour in the
 * same row, rather than the design-system `DropdownMenu`, so every control in
 * that row opens and closes the same way.
 *
 * The trigger shows an icon and the CURRENT LEVEL, not the word "Effort" and
 * the level. Two words for one control cost more room than the row has, and the
 * level is the half a reader actually scans. The word survives where it still
 * has to: `aria-label` names the control and its value out loud, and the open
 * menu is labelled `Effort`, so nothing is lost to a screen reader.
 */

interface EffortOption {
  value: AssistantEffort
  label: string
  icon: IconComponent
}

const EFFORT_OPTIONS: Array<EffortOption> = [
  { value: 'low', label: 'Low', icon: GaugeLow },
  { value: 'medium', label: 'Medium', icon: GaugeMedium },
  { value: 'high', label: 'High', icon: GaugeHigh },
]

interface EffortToggleProps {
  value: AssistantEffort
  onChange: (value: AssistantEffort) => void
  appearance?: ComposerAppearance
  className?: string
}

export function EffortToggle(props: EffortToggleProps) {
  const { value, onChange, appearance = 'external', className } = props

  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const select = (next: AssistantEffort) => {
    onChange(next)
    setIsOpen(false)
  }

  const activeOption = EFFORT_OPTIONS.find((option) => option.value === value)
  const activeLabel = activeOption?.label ?? 'Medium'
  // The dial's needle follows the level, so the chip reads Low / Medium / High at a glance.
  const ActiveIcon = activeOption?.icon ?? GaugeMedium

  return (
    <div ref={rootRef} className={cn('relative flex shrink-0 items-center', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`Effort: ${activeLabel}`}
        className={composerTriggerClassName(appearance, { isOpen })}
        data-testid="effort-toggle"
      >
        <ActiveIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {/* Inherits the trigger's own colour rather than forcing `text-foreground`.
            The darker weight used to separate the VALUE from the word "Effort" beside
            it; with the word gone there is nothing to separate it from, and it only
            made this one chip louder than Commands, Sources and Workflows in the
            same row. */}
        <span className={composerTriggerCaptionClassName(appearance)}>
          {activeLabel}
        </span>
        <ChevronDown
          className={cn(
            'h-3 w-3 shrink-0 opacity-70 transition-transform duration-200',
            composerTriggerChevronClassName(appearance),
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div
          role="menu"
          aria-label="Effort"
          /* Opens UPWARD: both mounts sit under their composer, at the bottom
             of the viewport, where a menu dropping down would open off-screen. */
          className={cn(
            'absolute bottom-full left-0 z-50 mb-2 w-[160px] rounded-xl p-1',
            composerPanelClassName(appearance),
          )}
          data-testid="effort-toggle-menu"
        >
          {EFFORT_OPTIONS.map((option) => {
            const isActive = option.value === value

            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => select(option.value)}
                className={cn(
                  'flex w-full items-center rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors',
                  'hover:bg-muted/70',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
                data-testid={`effort-toggle-${option.value}`}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
